import { ArrowLeftIcon } from '@radix-ui/react-icons'
import React, { useContext } from 'react'

import { type MoneyAmount } from '../types'
import { getLocaleCurrencyStringFromMoneyAmount } from '../utils'
import { CoverageCorrectionsTooltip } from './EmployerInvoiceBillingSummary'
import {
  EmployerInvoiceContext,
  type EnrichedMemberInvoice,
} from '../utils/employer_invoice_utils'

type CostSummaryType = {
  title: string;
  amount: MoneyAmount;
  hideIfZero?: boolean;
  isTotal?: boolean;
}

type CostSummaryProps = {
  data: CostSummaryType[];
  testId: string;
}

const EmployerInvoiceMemberSummary = ({
  memberInvoice,
}: {
  memberInvoice: EnrichedMemberInvoice;
}) => {
  const { setTargetMemberInvoice } = useContext(EmployerInvoiceContext)
  const { amounts } = memberInvoice

  const premiumsData: CostSummaryType[] = [
    {
      title: 'Upcoming Medical Premium',
      amount: memberInvoice.medical_premiums_total,
      hideIfZero: true,
    },
    {
      title: 'Upcoming Dental Premium',
      amount: memberInvoice.dental_premiums_total,
      hideIfZero: true,
    },
    {
      title: 'Upcoming Vision Premium',
      amount: memberInvoice.vision_premiums_total,
      hideIfZero: true,
    },
    {
      title: 'Total Scheduled Payments',
      amount: amounts.total_insurance_premiums,
      isTotal: true,
    },
  ]

  const otherData: CostSummaryType[] = [
    { title: 'Thatch qualified medical expense', amount: memberInvoice.pretax_employer_qme_reimbursement },
    {
      title: 'Ineligible spend',
      amount: memberInvoice.posttax_employee_qme_overage,
    },
    { title: 'Total Other', amount: amounts.other, isTotal: true },
  ]

  return (
    <>
      <div
        className='flex content-center text-indigo-500 text-sm cursor-pointer pb-4 w-48'
        onClick={() => setTargetMemberInvoice(null)}
      >
        <ArrowLeftIcon className='self-center' />
        <span className='pl-2'>Billing Statement</span>
      </div>
      <div className='flex flex-col' style={{ width: '50rem' }}>
        <MemberSummaryHeader name={memberInvoice.full_name} planYear={memberInvoice.plan_year} />
        <MemberSummarySubHeader>Member Expense Summary</MemberSummarySubHeader>

        <hr />

        {/* Expense Breakdown */}

        <div className='py-4'>
          <MemberOverviewSectionHeader>
            Expense Breakdown
          </MemberOverviewSectionHeader>
          <MemberOverviewSectionSubHeader>
            Scheduled Payments
          </MemberOverviewSectionSubHeader>

          <CostSummary data={premiumsData} testId='scheduled-payments-list' />
        </div>

        <div className='py-4'>
          <MemberOverviewAmountRow key='coverage-corrections'>
            <MemberOverviewAmountTotalDescription>
              Coverage Corrections <CoverageCorrectionsTooltip />
            </MemberOverviewAmountTotalDescription>
            <MemberOverviewAmountTotal>
              {getLocaleCurrencyStringFromMoneyAmount(
                amounts.coverage_corrections
              )}
            </MemberOverviewAmountTotal>
          </MemberOverviewAmountRow>
        </div>

        <div className='py-4'>
          <MemberOverviewSectionSubHeader>Other</MemberOverviewSectionSubHeader>
          <CostSummary data={otherData} testId='other-list' />
        </div>

        <div className='py-4'>
          <MemberOverviewAmountRow key='coverage-corrections'>
            <MemberOverviewAmountTotalDescription>
              Total Expenses
            </MemberOverviewAmountTotalDescription>
            <MemberOverviewAmountTotal>
              {getLocaleCurrencyStringFromMoneyAmount(amounts.total)}
            </MemberOverviewAmountTotal>
          </MemberOverviewAmountRow>
        </div>
      </div>
    </>
  )
}

const CostSummary = ({ data, testId }: CostSummaryProps) => {
  return (
    <div data-testid={testId} className='flex flex-col'>
      {data.map((subsection) => {
        if (
          subsection.hideIfZero &&
          parseFloat(subsection.amount.value) === 0
        ) {
          return null
        }

        const RowComponent = subsection.isTotal
          ? MemberOverviewAmountTotalRow
          : MemberOverviewAmountRow
        const DescriptionComponent = subsection.isTotal
          ? MemberOverviewAmountTotalDescription
          : MemberOverviewAmountDescription
        const AmountComponent = subsection.isTotal
          ? MemberOverviewAmountTotal
          : MemberOverviewAmount

        return (
          <RowComponent key={subsection.title}>
            <DescriptionComponent>{subsection.title}</DescriptionComponent>
            <AmountComponent>
              {getLocaleCurrencyStringFromMoneyAmount(subsection.amount)}
            </AmountComponent>
          </RowComponent>
        )
      })}
    </div>
  )
}

const MemberSummaryHeader = ({ name, planYear }: { name: string, planYear: number | undefined }) => {
  return (
    <div className='flex flex-row justify-between'>
      <div className='text-2xl pb-2'>{name}</div>
      {planYear && <div className='text-2xl pb-2'>{planYear}</div>}
    </div>
  )
}

const MemberSummarySubHeader = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <div className='text-lg pb-4 uppercase text-slate-400'>{children}</div>
  )
}

const MemberOverviewSectionHeader = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <div className='text-xl pb-4'>{children}</div>
}

const MemberOverviewSectionSubHeader = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <div className='font-normal uppercase pb-2'>{children}</div>
}

const MemberOverviewAmountDescription = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <div className='pl-4 font-light'>{children}</div>
}

const MemberOverviewAmountTotalDescription = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <div className='font-semibold uppercase'>{children}</div>
}

const MemberOverviewAmount = ({ children }: { children: React.ReactNode }) => {
  return <div className='font-light'>{children}</div>
}

const MemberOverviewAmountTotal = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <div className='font-semibold'>{children}</div>
}

const MemberOverviewAmountRow = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <div className='flex flex-row align-center items-center justify-between'>
      {children}
    </div>
  )
}

const MemberOverviewAmountTotalRow = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <div className='flex flex-row align-center items-center justify-between pt-2'>
      {children}
    </div>
  )
}

export default EmployerInvoiceMemberSummary
